/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/camelcase */
import React, { useState, useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import Helmet from 'react-helmet';
import CabModifySearch from '../../components/CabModifySearch/CabModifySearch';
import WhyBookWithUs from '../../components/WhyBookWithUs/WhyBookWithUs';
import FaqSection from '../../components/Faq/Faq';
import RentalCabs from '../../components/RentalCabs/RentalCabs';
import TopCabsRoutes from '../../components/TopCabsRoutes/TopCabsRoutes';
import Footer from '../../components/Footer/Footer';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import PageHeader from '../../components/PageHeader/PageHeader';
import { trackRouteTemplateEvent } from '../../utils/omnitureTracking';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import AppContextProvider from '../../AppContext';
import RouteCabs from '../../components/RouteCabs/RouteCabs';
import ThingsToKnow from '../../components/ThingsToKnow/ThingsToKnow';
import { ThrowReporter } from 'io-ts/lib/ThrowReporter';
import '../CityOperatorTemplate/CityOperatorTemplate.css';
import { EnvironmentVariables } from '../../types';
import { getAppEnv } from '../../../config/env';
import { useKnowMoreDetails } from '../customHooks';
import '../MultiCityPage/MultiCityPage.css';
import {
  RouteTemplateData,
  RouteTemplateChecker
} from './RouteDataTypeChecker';
import LoginOverlay from '../../components/LoginOverlay';
import { RouteComponentProps } from 'react-router';
import CompactList from '../../components/CompactList/CompactList';
import {
  SEO_ROUTE_TEMPLATE,
  SEO_ROUTE_TEMPLATE_ISOMORPHIC
} from '../../constants/templateConstants';
import { RouteCabsV2FilteredCabs } from '../../components/RouteCabsV2FilteredCards/RouteCabsV2FilteredCabs';
import {
  detectUserActivity,
  isMobile,
  useSearchWidgetHooks,
  generateRequestId,
  isSEM,
  isCrawler,
  fetchAndLogXdmEvent
} from '../../utils/commonUtils';
import { Usp } from '../runtime-typechecker';
import AssistModal from '../../components/AssistModal';
import './RouteTemplate.css';
import { SCROLL } from '../../constants/miscellaneousConstants';
import { yeildToMainThread } from '../../scheduler';
import { makeServiceDeskRequest } from '../../cabsAPIs';
import InboundCallBox from '../../components/InboundCall/InboundCallBox';
import CallUsModal from '../../components/AssistModal/callUsModal';
import TripAssistant from '../TripAssistant';
import PackagesBottomSheet from '../../components/GenerateBanner/PackagesBottomSheet';
import PackagesModal from '../../components/PackagesModal/PackagesModal';
import PackageBanner from '../../components/PackageBanner/PackageBanner';
import { OUTSTATION_ROUND_TRIP } from '../../constants/tripTypes';
import { userActionDone } from '../../utils/displayUtils';
import TruncateText from '../../components/CompactText/TruncateText';
import RouteInfoTabs, {
  getRouteInfoTabFaqs
} from '../../components/RouteInfoTabs/RouteInfoTabs';
import RouteInfoGraphic from '../../components/RouteInfoGraphic/RouteInfoGraphic';
import RouteInfoCards from '../../components/RouteInfoCards/RouteInfoCards';
import CustomerReviews from '../../components/CustomerReviews/CustomerReviews';
import UserRatings from '../../components/UserRatings/UserRatings';
import AuthorBio from '../../components/AuthorBio/AuthorBio';
import { getCookieValue } from '../../userDetailsUtils';
// import { updateUrlWithModal } from '../../utils/modalUtils';
// import { logRttEventLogger } from '../../logRttRequestBuilder';

const env = getAppEnv();
const { PUBLIC_URL } = env.raw;
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;
const ASSISTED_FORM_DATA_LS = 'ASSISTED_FORM_DATA_LS';
const SEM_VALUE = 'SEM';
type PathParamsType = {
  routeId: string;
  operatorId: string;
};
type RouteTemplateProp = RouteComponentProps<PathParamsType> & {
  data: RouteTemplateData;
};

const hasModalInUrl = (): boolean => {
  if (typeof window === 'undefined') return false;
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('modal') === 'login';
};

const isLoggedIn = (): boolean => {
  const cookieVal = getCookieValue('mmt-auth');
  if (cookieVal) {
    return Boolean(cookieVal);
  }
  return false;
};
const hasSEMInUrl = (): boolean => {
  if (typeof window === 'undefined') return false;
  const searchParams = new URLSearchParams(window.location.search);
  const source = searchParams.get('source');
  const cmp = searchParams.get('cmp');
  return (
    Boolean(source?.includes(SEM_VALUE)) || Boolean(cmp?.includes(SEM_VALUE))
  );
};

export const getLocalStorageValue = (localStorageKey: string) => {
  try {
    if (typeof localStorage !== 'undefined') {
      const item = localStorage.getItem(localStorageKey);
      return item != null ? JSON.parse(item) : null;
    }
  } catch (ex) {
    console.error('Exception in getLocalStorageValue', ex);
  }
  return null;
};

export const getPokusValueFromLS = (
  pokusExperimentKey: string,
  defaultValue: boolean | number
) => {
  const cabsPokuData = getLocalStorageValue('cabs_pokus');
  try {
    if (cabsPokuData != null) {
      const pokusExperimentValue =
        cabsPokuData?.data?.perLobMap?.CAB?.metadataValues[pokusExperimentKey];
      return pokusExperimentValue;
    }
  } catch (err) {
    console.log(
      `Exception in getting pokus values from local storage for pokuskey: ${pokusExperimentKey}`,
      err
    );
  }
  return defaultValue;
};

const runtimeTypChecker = (data: RouteTemplateData) => {
  const result = RouteTemplateChecker.decode(data);
  if (result._tag !== 'Right') {
    // console.log(PathReporter.report(result));
    ThrowReporter.report(result);
  }
};

// Route Map GIF Component
const RouteMapGif = ({
  anantmapUrl
}: {
  anantmapUrl: string | null | undefined;
}) => {
  const [modifiedUrl, setModifiedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (anantmapUrl) {
      // Get parameters from browser URL
      const browserParams = new URLSearchParams(window.location.search);
      const showAnantmapUrl = browserParams.get('showAnantmapUrl');
      const browserZoom = browserParams.get('zoom');
      const browserWidth = browserParams.get('width');
      const browserHeight = browserParams.get('height');

      // If browser has any of these parameters, modify the anantmapUrl
      if (showAnantmapUrl) {
        if (browserZoom || browserWidth || browserHeight) {
          const anantUrlObj = new URL(anantmapUrl);
          const anantParams = anantUrlObj.searchParams;

          // Override with browser parameters if present
          if (browserZoom) anantParams.set('zoom', browserZoom);
          if (browserWidth) anantParams.set('width', browserWidth);
          if (browserHeight) anantParams.set('height', browserHeight);

          setModifiedUrl(anantUrlObj.toString());
        } else {
          // Use original URL if no browser parameters
          setModifiedUrl(anantmapUrl);
        }
      }
    }
  }, [anantmapUrl]);

  // Show loading state while URL is being constructed
  if (!modifiedUrl) {
    return null;
  }

  return (
    <section
      className="visibilityAuto"
      style={{ textAlign: 'center', margin: '20px 0' }}
    >
      <img
        src={modifiedUrl}
        alt="Route visualization from source to destination"
        style={{
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      />
    </section>
  );
};

const RouteTemplate = ({ data }: RouteTemplateProp) => {
  //   runtimeTypChecker(data);
  const isMobileDevice = useMediaQuery({
    query: '(max-device-width: 768px)'
  });

  const [
    selectedKnowMoreDetailsForMobile,
    setSelectedKnowMoreDetailsForMobile
  ] = useKnowMoreDetails(null, isMobileDevice);
  const delayedKnowMoreDetailsForMobile = async val => {
    await yeildToMainThread(() => {
      requestAnimationFrame(() => setSelectedKnowMoreDetailsForMobile(val));
    });
  };

  const [showAssistantIc, setShowAssistantIc] = useState(false);
  const [showWhatsAppIcon, setWhatsAppIcon] = useState(false);
  const [showCallUsIcon, setCallUsIcon] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [shouldShowPackages, setShouldShowPackages] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [
    isMobileLoginOverlayVisible,
    setIsMobileLoginOverlayVisible
  ] = useState(false);
  const [isMobileLoginWidgetVisible, setIsMobileLoginWidgetVisible] = useState(
    false
  );

  const toggleOverlay = (isVisible: boolean) => {
    setIsOverlayVisible(isVisible);
  };
  const toggleMobileLoginOverlay = (isVisible: boolean) => {
    setIsMobileLoginOverlayVisible(isVisible);
  };

  useEffect(() => {
    if (isMobileLoginWidgetVisible) {
      toggleMobileLoginOverlay(true);
    }
  }, [isMobileLoginWidgetVisible]);
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setShowWhatsAppTooltip(true);
  //   }, 1000);

  //   return () => {
  //     clearTimeout(timer);
  //     setShowWhatsAppTooltip(false);
  //   };
  // }, []);

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setShowTripAssistantTooltip(true);
  //   }, 1000);

  //   return () => {
  //     clearTimeout(timer);
  //     setShowTripAssistantTooltip(false);
  //   };
  // }, []);

  const setShowModalWithDelay = async val => {
    if (
      isMobile()
        ? showCallusModalMweb || showTripAssistantButton
        : showTripAssistantButtonForDweb
    ) {
      await yeildToMainThread(() =>
        requestAnimationFrame(() => setShowModal(val))
      );
    }
  };
  const { travelFor, pickUpTime, dropTime } = useSearchWidgetHooks(
    SEO_ROUTE_TEMPLATE
  );
  const {
    allowRoundtrip,
    packages,
    filters,
    forwardDateCount,
    isomorph,
    reviewLinkEnabled,
    topRoutesfromCity,
    topRoutesToCity,
    sourceCityData,
    destinationCityData,
    cabTypesAvailableForRent,
    topRoutesInfoFromSourceToDestination,
    topRoutesInfoFromSourceToDestinationIsomorphic,
    templateData,
    description,
    showQuickBookCabsButton,
    showTripAssistantButton = false,
    showTripAssistantButtonForDweb = false,
    hrPackages,
    interlinks,
    assistedFormOpenDelay = 15000,
    showWhatsappWidget,
    whatsappWidgetDeeplink,
    aggregateRating,
    schemaMinFare,
    routeInfoData,
    customerCareNumber = '',
    semCustomerCareNumber = '',
    semInboundCallNumber = '',
    inboundCallText,
    inboundCallNumber = '',
    showInboundCallNumberInDesktop,
    showWhatsAppForAssistedForm = false,
    showCallUsWidget = false,
    assistantWidgetText = 'You can Call Us or Chat via WhatsApp',
    showCallUsForAssistedForm = false,
    showCallusModalMweb = false,
    callusModalOpenDelay = 10000,
    showInboundNumberOnAssistedForm = false,
    couponInfo = null,
    showLeaveIntentForm = false,
    packageData,
    whatsappTooltipText,
    tripAssistanceToolTipText,
    openTripTypeAndThenDate,
    openDateWidget,
    openTripType,
    intentCaptureEnabled,
    intentCaptureType,
    anantmapUrl,
    routeMapViewUrl,
    ugcReviews,
    useAPILevelReviewSchema = 0
  } = data;

  const VARIANT_1 = 'BLOCK_PAGE_USE_REVIEW_SCHEMA';
  const VARIANT_2 = 'BLOCK_API_LEVEL_USE_SUMMARY_REVIEW_SCHEMA';

  let usedVariant: string | null = getBotVisibilityAndSchemaStrategy(
    useAPILevelReviewSchema,
    VARIANT_2,
    VARIANT_1
  );

  if (templateData?.sampleUrl?.includes('delhi-agra-cab-services.html')) {
    usedVariant = VARIANT_2;
  }

  let inboundCallNumberFinal = inboundCallNumber;
  let customerCareNumberFinal = customerCareNumber;
  if (isSEM()) {
    inboundCallNumberFinal = semInboundCallNumber;
    customerCareNumberFinal = semCustomerCareNumber;
  }
  const [showWhatsAppTooltip, setShowWhatsAppTooltip] = useState(
    Boolean(whatsappTooltipText)
  );
  const [showTripAssistantTooltip, setShowTripAssistantTooltip] = useState(
    Boolean(tripAssistanceToolTipText)
  );

  const tooltipDelay = packageData?.tooltipInfo?.delay || 3000;
  // check if url has
  // const seoRouteUrl1 = templateData.sampleUrl.includes('delhi-agra');
  // const seoRouteUrl2 = templateData.sampleUrl.includes('etah-agra');
  const {
    name: sourceCityName,
    code: sourceCityCode,
    description: sourceCityDescription
  } = sourceCityData;

  const {
    name: destinationCityName,
    code: destinationCityCode,
    description: destinationCityDescription
  } = destinationCityData;

  // const scrollPosition = useRef(0);
  // const assistedFormLifeCycle = useRef(0); // 0:init,1:scroll down to threshold, 2: scroll up to threshold, and automated modal shown

  // const showModalConsideringScrollEvents = () => {
  //   if (assistedFormLifeCycle.current != 2 && !userActionDone()) {
  //     assistedFormLifeCycle.current = 2;
  //     setShowModal(true);
  //     trackRouteTemplateEvent('Cab_SEO_Assisted_Form_Shown');
  //   }
  // };

  useEffect(() => {
    const url = typeof window !== 'undefined' ? window.location.href : data.url;
    fetchAndLogXdmEvent(url);
  }, []);

  useEffect(() => {
    if (showWhatsAppTooltip) {
      setTimeout(() => {
        setShowWhatsAppTooltip(false);
      }, tooltipDelay);
    }
    if (showTripAssistantTooltip) {
      setTimeout(() => {
        setShowTripAssistantTooltip(false);
      }, tooltipDelay);
    }
  });

  const isomorphicUspsForMobile = (() => {
    const isomorphicUspsForMobile: any = [];
    if (
      topRoutesInfoFromSourceToDestinationIsomorphic &&
      topRoutesInfoFromSourceToDestinationIsomorphic?.cabsList?.length > 0
    ) {
      const tripBenefits =
        topRoutesInfoFromSourceToDestinationIsomorphic?.tripBenefits;
      if (tripBenefits && tripBenefits?.length > 0) {
        tripBenefits.forEach((tripBenefit, idx) => {
          const usp: Usp = {
            uspDescription: String(tripBenefit.subTitle),
            uspTitle: String(tripBenefit.title),
            logoUrl: String(tripBenefit.icon),
            id: idx,
            created: null,
            modified: null,
            logoDTUrl: null
          };
          isomorphicUspsForMobile.push(usp);
        });
      }
    }
    return isomorphicUspsForMobile;
  })();
  const overridTemplateUspsWithTripBenefits =
    isomorph && isomorphicUspsForMobile && isomorphicUspsForMobile?.length > 0;

    
  useEffect(() => {
    window.addEventListener(SCROLL, listenToScroll);
    if (window?.localStorage) {
      localStorage.removeItem(ASSISTED_FORM_DATA_LS);
    }
    detectUserActivity();
    //Show assistant modal automatically after xx seconds
    const timeDelay = showCallusModalMweb
      ? callusModalOpenDelay
      : assistedFormOpenDelay;
    setTimeout(() => {
      const eventName =
        showCallusModalMweb && showCallUsForAssistedForm && isMobile()
          ? 'Cab_SEO_Assisted_Call_Us_Form_Shown'
          : 'Cab_SEO_Assisted_Form_Shown';
      if (window?.localStorage) {
        if (
          !userActionDone() &&
          (isMobile()
            ? showTripAssistantButton || showCallusModalMweb
            : showTripAssistantButtonForDweb)
        ) {
          setShowModalWithDelay(true);
          trackRouteTemplateEvent(eventName);
        } else {
          // not show message should not be shown if modal is already shown
          const userAction = userActionDone();
          trackRouteTemplateEvent(
            `Cab_SEO_Assisted_Form_Not_Shown_userActionDone_${userAction}`
          );
        }
      }
    }, timeDelay);
    // based on the tripAssistantOpen flag value open assisted form
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('tripAssistantOpen') === 'true') {
      setShowModal(true);
    }
    if (showCallUsWidget && isMobile()) {
      setTimeout(() => {
        trackRouteTemplateEvent('Cab_SEO_Call_Us_Icon_Shown');
      }, 500);
    }

    setTimeout(() => {
      // // Ensure browser APIs are fully loaded before calling updateUrlWithModal
      const loggedIn = isLoggedIn();
      const loginNudge = getPokusValueFromLS('Login_Nudge', 0);
      const hasModal = hasModalInUrl();
      const hasSEM = hasSEMInUrl();

      if (hasSEM && isMobileDevice && loginNudge && !loggedIn) {
        setTimeout(() => {
          setIsMobileLoginWidgetVisible(true);
        }, 1000);
      }

      try {
        // Ensure all required browser APIs are available
        if (typeof window !== 'undefined') {
          // Additional check to ensure URLSearchParams is available
          if (typeof URLSearchParams !== 'undefined') {
            // console.log('loginNudge', loginNudge);
            if (
              !hasModal &&
              hasSEM &&
              !isMobile() &&
              loginNudge === 1 &&
              !loggedIn
            ) {
              const url = new URL(window.location.href);
              url.searchParams.set('modal', 'login');
              window.history.replaceState({}, '', url.toString());
            }
          }
        }
      } catch (error) {
        console.warn('Error in updateUrlWithModal:', error);
      }
    }, 1000);

    if (intentCaptureEnabled) {
      setTimeout(() => {
        trackRouteTemplateEvent(
          'Cab_SEO_Intent_Capture_Shown_' + intentCaptureEnabled
        );
      }, 2000);
    }
    return () => window.removeEventListener(SCROLL, listenToScroll);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !routeMapViewUrl || isMobileDevice) {
      return;
    }

    let observer: IntersectionObserver | null = null;
    let shownObserver: IntersectionObserver | null = null;
    const setupObserver = () => {
      const iframe = document.querySelector(
        '.routeMapIframeSection iframe'
      ) as HTMLIFrameElement;

      if (!iframe || iframe.getAttribute('src')) {
        return;
      }

      if (isCrawler()) {
        iframe.setAttribute('src', routeMapViewUrl);
        return;
      }
      observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (entry.isIntersecting && !iframe.getAttribute('src')) {
              iframe.setAttribute('src', routeMapViewUrl);
              if (observer) {
                observer.disconnect();
                observer = null;
              }
              break;
            }
          }
        },
        {
          rootMargin: '750px'
        }
      );

      observer.observe(iframe);

      shownObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            trackRouteTemplateEvent('Cab_SEO_Route_Map_Shown');
            if (shownObserver) {
              shownObserver.disconnect();
              shownObserver = null;
            }
            break;
          }
        }
      });
      shownObserver.observe(iframe);
    };
    if (
      document.readyState === 'complete' ||
      document.readyState === 'interactive'
    ) {
      setupObserver();
    } else {
      window.addEventListener('DOMContentLoaded', setupObserver, {
        once: true
      });
    }

    const handleIframeClick = () => {
      setTimeout(() => {
        const iframe = document.querySelector(
          '.routeMapIframeSection iframe'
        ) as HTMLIFrameElement;
        if (
          iframe &&
          iframe.getAttribute('src') &&
          document.activeElement === iframe
        ) {
          trackRouteTemplateEvent('Cab_SEO_Route_Map_Clicked');
          window.focus();
        }
      }, 0);
    };

    window.addEventListener('blur', handleIframeClick);

    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (shownObserver) {
        shownObserver.disconnect();
      }
      window.removeEventListener('blur', handleIframeClick);
    };
  }, [routeMapViewUrl, isMobileDevice]);

  const listenToScroll = () => {
    const heightToShowFrom = 100;
    const winScroll =
      document.body.scrollTop || document.documentElement.scrollTop;
    if (winScroll > heightToShowFrom) {
      if (!(isMobileDevice && showModal)) {
        setShowAssistantIc(true);
        setWhatsAppIcon(true);
        setCallUsIcon(true);
      }
    } else {
      setShowAssistantIc(false);
      setWhatsAppIcon(false);
      setCallUsIcon(false);
    }
  };

  const fabInteraction = () => {
    return (
      <div
        className={` ${(() => {
          if (showTripAssistantButtonForDweb && showTripAssistantButton) {
            return '';
          } else if (showTripAssistantButton) {
            return 'mobileShow';
          } else if (showTripAssistantButtonForDweb) {
            return 'desktopShow';
          }
          return '';
        })()}`}
      >
        <div
          onClick={() => {
            trackRouteTemplateEvent(
              `${
                showModal
                  ? 'Cab_SEO_Assisted_Form_Closed'
                  : 'Cab_SEO_Assisted_Form_CTA_Click'
              }`
            );
            setShowModalWithDelay(!showModal);
          }}
          className={`fabInteraction ${
            showModal && isMobileDevice
              ? 'showCrossIc'
              : showAssistantIc
              ? 'showIc'
              : ''
          }`}
        >
          <div className={`icContainer ${showModal ? 'showCrossIc' : ''}`} />
          {true && <span>Trip Assistant</span>}
        </div>
      </div>
    );
  };

  const callUsForm = () => {
    return (
      <div>
        <div
          onClick={() => {
            trackRouteTemplateEvent(
              `${
                showModal
                  ? 'Cab_SEO_Assisted_Call_Us_Form_Closed'
                  : 'Cab_SEO_Assisted_Form_CTA_Click'
              }`
            );
            setShowModalWithDelay(!showModal);
          }}
          className={`${
            showModal && isMobileDevice
              ? 'fabInteraction showCrossIc'
              : showAssistantIc
              ? 'showIc'
              : ''
          }`}
        >
          <div className={`icContainer ${showModal ? 'showCrossIc' : ''}`} />
        </div>
      </div>
    );
  };

  const handleTooltipClose = e => {
    e.stopPropagation();
    setShowWhatsAppTooltip(false);
  };

  const handleWhatsappWidget = () => {
    onWhatsAppIconClick();
    trackRouteTemplateEvent('Cab_SEO_WhatsApp_Assisted_Icon_Clicked');
    setShowWhatsAppTooltip(false);
  };

  const onWhatsAppIconClick = () => {
    const requestId = generateRequestId();
    const pageName = 'seo_route_page';
    makeServiceDeskRequest(requestId, pageName);
    const whatsAppChatLinkWithRequestId = whatsappWidgetDeeplink
      ? whatsappWidgetDeeplink.replace('MDRequest_id', requestId)
      : '';
    if (whatsAppChatLinkWithRequestId)
      window.location.href = whatsAppChatLinkWithRequestId;
  };

  const onCallUsIconClick = () => {
    window.open(`tel:${customerCareNumberFinal}`, '_blank');
  };

  const handleClose = () => {
    setIsBottomSheetOpen(false);
    // logRttEventLogger({omniUrlArgs:`source=${
    //     isomorph ? SEO_ROUTE_TEMPLATE_ISOMORPHIC : SEO_ROUTE_TEMPLATE
    //   }`, eventName: 'Cab_Packages_Bottom_Sheet_Closed', pageUrl: templateData.sampleUrl });

    trackRouteTemplateEvent('Cab_Packages_Bottom_Sheet_Closed');
  };
  useEffect(() => {
    if (packageData && packageData?.show_delay) {
      const timer = setTimeout(() => {
        // const ModalOrSheetOpened = window.sessionStorage.getItem(
        //   'packageModalOrSheetOpenedRoute'
        // );
        // if (!ModalOrSheetOpened) {
        if (isMobile()) {
          // logRttEventLogger({omniUrlArgs:`source=${
          //   isomorph ? SEO_ROUTE_TEMPLATE_ISOMORPHIC : SEO_ROUTE_TEMPLATE
          // }`, eventName: 'Cab_Packages_Bottom_Sheet_Shown',pageUrl:templateData.sampleUrl });

          trackRouteTemplateEvent('Cab_Packages_Bottom_Sheet_Shown');
          setIsBottomSheetOpen(packageData.show_bottom_sheet_mweb);
        } else {
          //      logRttEventLogger({omniUrlArgs:`source=${
          //   isomorph ? SEO_ROUTE_TEMPLATE_ISOMORPHIC : SEO_ROUTE_TEMPLATE
          // }`, eventName: 'Cab_Packages_Modal_Shown',pageUrl:templateData.sampleUrl });

          trackRouteTemplateEvent('Cab_Packages_Modal_Shown');
          setShouldShowPackages(packageData.show_modal_dweb);
        }
        //   window.sessionStorage.setItem(
        //     'packageModalOrSheetOpenedRoute',
        //     'true'
        //   );
        // }
      }, packageData.show_delay);

      return () => clearTimeout(timer);
    }
  }, [packageData]);

  templateData.faq.map(faq => {
    if (faq.answer.includes('0124 - 4781861')) {
      faq.answer = faq.answer.replace(
        '0124 - 4781861',
        customerCareNumberFinal
      );
    }
  });
  return (
    <AppContextProvider
      omniUrlArgs={`source=${
        isomorph ? SEO_ROUTE_TEMPLATE_ISOMORPHIC : SEO_ROUTE_TEMPLATE
      }`}
      trackTemplateEvent={trackRouteTemplateEvent}
      selectedKnowMoreDetailsForMobile={selectedKnowMoreDetailsForMobile}
      setSelectedKnowMoreDetailsForMobile={delayedKnowMoreDetailsForMobile}
    >
      {isMobileLoginWidgetVisible && isMobileDevice && (
        <LoginOverlay
          onClose={() => {
            setIsMobileLoginOverlayVisible(false);
            setIsMobileLoginWidgetVisible(false);
          }}
        />
      )}
      <div>
        {isMobile() && showWhatsappWidget && (
          <div className="whatsapp-wrapper">
            {showWhatsAppTooltip && (
              <div className="whatsapp-tooltip">
                <span className="whatsapp-tooltip-text">
                  {packageData?.tooltipInfo?.text || 'Chat with us on WhatsApp'}
                </span>
                <img
                  onClick={handleTooltipClose}
                  src={`${PUBLIC_URL}/static/public/cross_white_icon.png`}
                  alt="close"
                  className="whatsapp-tooltip-close"
                />
              </div>
            )}
            <div
              className={`${!showModal ? 'whatsApp' : ''} ${
                showModal ? '' : showWhatsAppIcon ? 'showWhatsAppIcon' : ''
              }`}
              onClick={handleWhatsappWidget}
            >
              <div
                className={`${!showModal ? 'whatsaAppIconContainer' : ''}`}
              />
              {true && !showModal && <span>Chat with us</span>}
            </div>
          </div>
        )}
      </div>
      <div>
        {isMobile() && showCallUsWidget && customerCareNumberFinal && (
          <div
            className={`${!showModal ? 'callUs' : ''} ${
              showModal ? '' : showCallUsIcon ? 'showCallUsIcon' : ''
            }`}
            onClick={() => {
              onCallUsIconClick();
              trackRouteTemplateEvent('Cab_SEO_Call_Us_Icon_Clicked');
            }}
          >
            <div className={`${!showModal ? 'callUsIconContainer' : ''}`} />
            {true && !showModal && <span>Call us now</span>}
          </div>
        )}
      </div>
      {isMobile() && showTripAssistantButton ? fabInteraction() : <div></div>}
      {(showTripAssistantButtonForDweb || showLeaveIntentForm) &&
      !isMobile() ? (
        <div className="tripAssistant">
          {showTripAssistantTooltip && (
            <div className="tripassistant-tooltip">
              <span className="tripassistant-tooltip-text">
                {packageData?.tooltipInfo?.text}
              </span>
              <img
                onClick={e => {
                  e.stopPropagation();
                  setShowTripAssistantTooltip(false);
                }}
                src={`${PUBLIC_URL}/static/public/cross_white_icon.png`}
                alt="close"
                className="tripassistant-tooltip-close"
              />
            </div>
          )}
        </div>
      ) : (
        <div className="tripAssistant"></div>
      )}
      {(showTripAssistantButtonForDweb || showLeaveIntentForm) &&
      !isMobile() ? (
        <div className="tripAssistant2">
          <TripAssistant
            trackEvent={trackRouteTemplateEvent}
            autoOpenDelay={assistedFormOpenDelay}
            source={sourceCityData?.name}
            destination={destinationCityData?.name}
            couponData={couponInfo}
            hideForm={false}
            customerCareNumber={inboundCallNumberFinal}
            showTripAssistant={showTripAssistantButtonForDweb}
            showLeaveIntentForm={showLeaveIntentForm}
          />
        </div>
      ) : (
        <div className="tripAssistant2"></div>
      )}
      {showCallusModalMweb && callUsForm()}
      {showModal && isMobile() && showTripAssistantButton && (
        <AssistModal
          sourceCity={sourceCityData?.name}
          destinationCity={destinationCityData?.name}
          setShowModal={val => setShowModalWithDelay(val)}
          trackTemplateEvent={trackRouteTemplateEvent}
          showWhatsAppForAssistedForm={showWhatsAppForAssistedForm}
          showCallUsForAssistedForm={showCallUsForAssistedForm}
          customerCareNumber={customerCareNumberFinal}
          assistantWidgetText={assistantWidgetText}
          onCallUsIconClick={onCallUsIconClick}
          onWhatsAppIconClick={onWhatsAppIconClick}
          showInboundNumberOnAssistedForm={showInboundNumberOnAssistedForm}
          inboundCallNumber={inboundCallNumberFinal}
          inboundCallText={inboundCallText}
        />
      )}
      {showModal && isMobile() && showCallusModalMweb && (
        <CallUsModal
          setShowModal={val => setShowModalWithDelay(val)}
          trackTemplateEvent={trackRouteTemplateEvent}
          showWhatsAppForAssistedForm={showWhatsAppForAssistedForm}
          showCallUsForAssistedForm={showCallUsForAssistedForm}
          customerCareNumber={customerCareNumberFinal}
          assistantWidgetText={assistantWidgetText}
          onCallUsIconClick={onCallUsIconClick}
          onWhatsAppIconClick={onWhatsAppIconClick}
          showModal={showModal}
        />
      )}
      {packageData && isMobile() && isBottomSheetOpen && !userActionDone() ? (
        <PackagesBottomSheet
          packageData={packageData}
          onClose={handleClose}
          pageUrl={templateData.sampleUrl}
        />
      ) : null}
      {packageData && !isMobile() && shouldShowPackages && !userActionDone() && (
        <PackagesModal
          pageUrl={templateData.sampleUrl}
          packageData={packageData}
          onClose={() => {
            setShouldShowPackages(false);
            // logRttEventLogger({omniUrlArgs:`source=${
            //   isomorph ? SEO_ROUTE_TEMPLATE_ISOMORPHIC : SEO_ROUTE_TEMPLATE
            // }`, eventName: 'Cab_Packages_Modal_Closed', pageUrl: templateData.sampleUrl });

            trackRouteTemplateEvent('Cab_Packages_Modal_Closed');
          }}
          show={true}
        />
      )}
      <div
        className={`cabSeoDtPage ${
          // !collapsableFlag || selectedKnowMoreDetailsForMobile ? 'expanded' :''
          selectedKnowMoreDetailsForMobile ? 'expanded' : ''
        }`}
      >
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta
            id="meta-description"
            name="description"
            content={templateData.metaDescription}
          />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
          {templateData.ogTitle && (
            <meta property="og:title" content={templateData.ogTitle} />
          )}
          {templateData.ogDescription && (
            <meta
              property="og:description"
              content={templateData.ogDescription}
            />
          )}
          {templateData.ogImage && (
            <meta name="og:image" content={templateData.ogImage} />
          )}
          {templateData.twitterTitle && (
            <meta name="twitter:title" content={templateData.twitterTitle} />
          )}
          {templateData.twitterDescription && (
            <meta
              name="twitter:description"
              content={templateData.twitterDescription}
            />
          )}
          {templateData.twitterImage && (
            <meta name="twitter:image" content={templateData.twitterImage} />
          )}
        </Helmet>
        {aggregateRating && schemaMinFare && (
          <Helmet>
            <script type="application/ld+json">
              {`
                {
                  "@context": "https://schema.org",
                  "@type": "Product",
                  "name":"${sourceCityData.name} to ${
                destinationCityData.name
              } Cabs",
                  "category":"Cab Services",
                  "url":"${templateData.sampleUrl}",
                  "description":"${
                    templateData.metaDescription
                      ? templateData.metaDescription.replace(/"/g, "'")
                      : ''
                  }",
                  "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": "${aggregateRating.rating}",
                    "reviewCount": "${aggregateRating.ratingCount}",
                    "itemReviewed": {
                    "@type":"Car",
                    "name":"${sourceCityData.name} to ${
                destinationCityData.name
              } Cabs"
                    }
                  },
                  "audience": {
                  "@type":"audience",
                  "description":"${aggregateRating.ratingCount} Reviews",
                  "audienceType" : "Customer"
                  },
                  "offers": {
                  "@type": "AggregateOffer",
                  "lowPrice": "${schemaMinFare}",
                  "priceCurrency": "INR"
                  }
                }
              `}
            </script>
            <script type="application/ld+json">
              {templateData.serviceSchema}
            </script>
            <script type="application/ld+json">
              {templateData.tripSchema}
            </script>
            <script type="application/ld+json">
              {templateData.webPageSchema}
            </script>
          </Helmet>
        )}
        {usedVariant === VARIANT_1 || usedVariant === VARIANT_2 ? (
          <Helmet>
            <script type="application/ld+json">
              {`
              {
                "@context": "https://schema.org",
                "@type": "Organization",
                "name": "MakeMyTrip",
                "url": "https://www.makemytrip.com",
                "logo": "https://imgak.mmtcdn.com/pwa_v3/pwa_header_assets/mmt_logo.png",
                "sameAs": [
                  "https://www.facebook.com/makemytrip",
                  "https://twitter.com/makemytrip",
                  "https://www.instagram.com/makemytrip",
                  "https://www.linkedin.com/company/makemytrip.com",
                  "https://www.trustpilot.com/review/www.makemytrip.com"
                ],
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": "0124 462 8747",
                  "contactType": "customer service",
                  "availableLanguage": ["English", "Hindi"]
                },
                "knowsAbout": ["Inter-city cab travel", "Outstation cab booking", "${sourceCityName} to ${destinationCityName} cabs"]
              }
            `}
            </script>
            {/* {ugcReviews?.bestReviews?.length > 0 && usedVariant===VARIANT_1 && (
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'Product',
                name: `${sourceCityName} to ${destinationCityName} Cab Booking`,
                description: `Book a cab from ${sourceCityName} to ${destinationCityName} on MakeMyTrip. Transparent pricing, verified drivers, and 24/7 support.`,
                review: ugcReviews?.bestReviews?.slice(0, 5).map((r: any) => ({
                  '@type': 'Review',
                  reviewRating: {
                    '@type': 'Rating',
                    ratingValue: parseFloat(r.overallRating),
                    bestRating: 5
                  },
                  author: { '@type': 'Person', name: r.reviewerName || 'Anonymous' },
                  reviewBody: r.review,
                  datePublished: r.reviewDate?.split(' ')[0]
                })),
                ...(aggregateRating && {
                  aggregateRating: {
                    '@type': 'AggregateRating',
                    ratingValue: aggregateRating.rating,
                    reviewCount: aggregateRating.ratingCount
                  }
                })
              })}
            </script>
          )} */}
          </Helmet>
        ) : null}
        <div className={`desktopShow makeSticky`}>
          <CabModifySearch
            intentCaptureType={intentCaptureType}
            isOverlayVisible={isOverlayVisible}
            openDateWidget={openDateWidget}
            openTripType={openTripType}
            openTripTypeAndThenDate={openTripTypeAndThenDate}
            pickupTime={pickUpTime}
            defaultTripType={allowRoundtrip ? OUTSTATION_ROUND_TRIP : travelFor}
            returnTime={dropTime}
            returnCloseFlagDefault={false}
            sourceCityCode={sourceCityCode}
            destinationCityCode={destinationCityCode}
            sourceCityName={sourceCityName}
            destinationCityName={destinationCityName}
            isMobileDevice={false}
            hrPackages={hrPackages}
            title={`${sourceCityName} to ${destinationCityName} Cabs`}
            isomorph={isomorph}
          />
        </div>

        <div className={`domChanger mobileShow makeSticky`}>
          <CabModifySearch
            intentCaptureType={intentCaptureType}
            isOverlayVisible={isOverlayVisible}
            overlayToggle={toggleOverlay}
            openDateWidget={openDateWidget}
            openTripType={openTripType}
            openTripTypeAndThenDate={openTripTypeAndThenDate}
            pickupTime={pickUpTime}
            defaultTripType={allowRoundtrip ? OUTSTATION_ROUND_TRIP : travelFor}
            returnTime={dropTime}
            returnCloseFlagDefault={false}
            sourceCityCode={sourceCityCode}
            destinationCityCode={destinationCityCode}
            sourceCityName={sourceCityName}
            destinationCityName={destinationCityName}
            isMobileDevice={true}
            title={`${sourceCityName} to ${destinationCityName} Cabs`}
            isomorph={isomorph}
            hrPackages={hrPackages}
            showQuickBookCabsButton={showQuickBookCabsButton}
          />
        </div>
        {isomorph &&
          topRoutesInfoFromSourceToDestinationIsomorphic?.cabsList?.length && (
            <RouteCabsV2FilteredCabs
              packageData={packageData}
              packages={packages}
              sourceCityCode={sourceCityCode}
              destinationCityCode={destinationCityCode}
              isMobileDevice={isMobile()}
              data={topRoutesInfoFromSourceToDestinationIsomorphic}
              filters={filters}
              sourceCityName={sourceCityName}
              destinationCityName={destinationCityName}
              forwardDateCount={forwardDateCount || 0}
              templateData={templateData}
              inboundCallText={inboundCallText}
              inboundCallNumber={inboundCallNumberFinal}
              showInboundCallNumberInDesktop={showInboundCallNumberInDesktop}
            />
          )}
        <main className="container">
          <div>
            {!isomorph ? (
              <div>
                <div className="makeFlex">
                  <BreadCrumb breadcrumb={templateData.breadcrumb} />
                  {showInboundCallNumberInDesktop ? (
                    <InboundCallBox
                      inboundCallText={inboundCallText}
                      inboundCallNumber={inboundCallNumberFinal}
                    />
                  ) : (
                    <div></div>
                  )}
                </div>
                {/* <PageHeader
                  title={templateData.h1Title}
                  extraInfo={templateData.metaDescription}
                  isMobileDevice={isMobileDevice}
                  url={templateData.sampleUrl}
                /> */}
                <PageHeader
                  title={templateData.h1Title}
                  isHtml={true}
                  extraInfo={templateData.extraInfo}
                  isMobileDevice={isMobileDevice}
                  viewMoreHtml={templateData.viewMore}
                  viewLessHtml={templateData.viewLess}
                />
              </div>
            ) : null}
            {/* {!isomorph ? (
              <div>
                <div>
                  <AdsComponent
                    template={'Route'}
                    placedAt={'top'}
                    isMobileDevice={isMobileDevice}
                    desktopAdConfig={{
                      position: templateData.bannerDesktop
                        ? templateData.bannerDesktop
                        : 'none',
                      class: 'desktopShow appendBottom20 adsLayout',
                      style: {}
                    }}
                    mobileAdConfig={{
                      position: templateData.bannerMobile
                        ? templateData.bannerMobile
                        : 'none',
                      class: 'mobileShow appendBottom20 adsLayout',
                      style: { marginTop: null }
                    }}
                  />
                </div>
              </div>
            ) : null} */}
            {/* {!isomorph && packages && packages.length > 0 ? (
              <div>
                <h2 className="blackText">{`Popular packages from ${sourceCityName} to ${destinationCityName}`}</h2>
                <br />
                <MultiCityPackages
                  removeLeftRightPadding={true}
                  packages={packages}
                  disableStopFilter={true}
                  disableCabCardImage={true}
                  stopType={'city'}
                />
              </div>
            ) : null} */}
            {!isomorph && packageData && (
              <PackageBanner
                packageData={packageData}
                pageUrl={templateData.sampleUrl}
              />
            )}
            {!isomorph &&
            !packageData &&
            templateData.viewMore &&
            templateData.viewLess ? (
              <div id="summary" className="pageExtraInfo font15 appendTop5">
                <TruncateText
                  whiteBackground={false}
                  isHtml={true}
                  viewMoreHtml={templateData.viewMore}
                  viewLessHtml={templateData.viewLess}
                  text={templateData.viewLess}
                  isMobileDevice={isMobileDevice}
                  textLength={0}
                />
              </div>
            ) : (
              <div></div>
            )}
            {topRoutesInfoFromSourceToDestination.cabsList.length > 0 &&
            !isomorph ? (
              <section>
                {isMobileDevice ? (
                  <>
                    <h2
                      style={{ marginBottom: '0px' }}
                    >{`${sourceCityName} to ${destinationCityName} ${
                      allowRoundtrip ? 'round trip' : 'one way'
                    } distance  ${
                      topRoutesInfoFromSourceToDestination.oneWayDistance
                    } kms`}</h2>
                    <h2>{`${
                      allowRoundtrip ? 'Round trip' : 'One way'
                    } duration ${
                      topRoutesInfoFromSourceToDestination.oneWayDurationInHrMins
                    }`}</h2>
                  </>
                ) : (
                  <h2>{`${sourceCityName} to ${destinationCityName} ${
                    allowRoundtrip ? 'round trip' : 'one way'
                  } distance ${
                    topRoutesInfoFromSourceToDestination.oneWayDistance
                  } kms | ${
                    allowRoundtrip ? 'Round trip' : 'One way'
                  } duration ${
                    topRoutesInfoFromSourceToDestination.oneWayDurationInHrMins
                  }`}</h2>
                )}
                {/* <BNPLSection isMobileDevice={isMobileDevice} /> */}
                <RouteCabs
                  data={
                    reviewLinkEnabled
                      ? topRoutesInfoFromSourceToDestinationIsomorphic?.cabsList
                      : topRoutesInfoFromSourceToDestination?.cabsList
                  }
                  reviewLinkEnabled={reviewLinkEnabled}
                  seoUrl={topRoutesInfoFromSourceToDestination.seoUrl}
                  sourceCityName={sourceCityName}
                  destinationCityName={destinationCityName}
                />
              </section>
            ) : (
              <section className="hideSection"></section>
            )}
            {/* <RouteInfoGraphic
              sourceCityName={sourceCityName}
              destinationCityName={destinationCityName}
              inboundCallNumber={inboundCallNumberFinal}
            /> */}
            <RouteMapGif anantmapUrl={anantmapUrl} />
            {packageData && templateData.viewMore && templateData.viewLess ? (
              <div id="summary" className="pageExtraInfo font15 appendTop5">
                <TruncateText
                  whiteBackground={false}
                  isHtml={true}
                  viewMoreHtml={templateData.viewMore}
                  viewLessHtml={templateData.viewLess}
                  text={templateData.viewLess}
                  isMobileDevice={isMobileDevice}
                  textLength={0}
                />
              </div>
            ) : (
              <div></div>
            )}
            {usedVariant === VARIANT_2 || usedVariant === VARIANT_1 ? (
              <>
                <RouteInfoTabs
                  sourceCityName={sourceCityName}
                  destinationCityName={destinationCityName}
                  inboundCallNumber={inboundCallNumber}
                  pageUrl={templateData.sampleUrl}
                  omniUrlArgs={`source=${
                    isomorph
                      ? SEO_ROUTE_TEMPLATE_ISOMORPHIC
                      : SEO_ROUTE_TEMPLATE
                  }`}
                />
                <br />
              </>
            ) : null}
            {usedVariant === VARIANT_2 || usedVariant === VARIANT_1 ? (
              <UserRatings
                sourceCityName={sourceCityName}
                destinationCityName={destinationCityName}
                sourceCityCode={sourceCityCode}
                destinationCityCode={destinationCityCode}
                ugcReviews={ugcReviews}
                overallRating={aggregateRating?.rating}
              />
            ) : null}
            <br />
            {routeMapViewUrl && !isMobileDevice && (
              <>
                <section className="routeMapIframeSection">
                  <h2>{`${sourceCityName} to ${destinationCityName}: Explore What Lies Along the Route`}</h2>
                  <iframe
                    data-src={routeMapViewUrl}
                    width="1200"
                    height="540"
                    title={`${sourceCityName} to ${destinationCityName}: Explore What Lies Along the Route`}
                  />
                </section>
              </>
            )}
            {/* {usedVariant===VARIANT_2 && (
            <CustomerReviews
              sourceCityName={sourceCityName}
              destinationCityName={destinationCityName}
              sourceCityCode={sourceCityCode}
              destinationCityCode={destinationCityCode}
              overallRating={aggregateRating?.rating}
            />
            )} */}
            <div>
              {// ((isomorph && isMobileDevice) || !isomorph) &&
              templateData?.usps?.length > 0 && (
                <section
                  style={{ display: 'block' }}
                  className={` bookWithUsWrap ${isomorph ? '' : ''} `}
                >
                  <h2>
                    {// isMobileDevice?
                    `Why Book  with Us`
                    // : `Why to Book ${sourceCityName} to ${destinationCityName} Cabs with Us`
                    }
                  </h2>
                  <WhyBookWithUs
                    useDirectSourceUrl={overridTemplateUspsWithTripBenefits}
                    usps={
                      overridTemplateUspsWithTripBenefits
                        ? isomorphicUspsForMobile
                        : templateData.usps
                    }
                  />
                </section>
              )}
            </div>
            {/* <div
              className="routeSvgInfographic"
              style={{ margin: '20px 0', textAlign: 'center' }}
            >
              <svg
                width="800"
                height="1000"
                viewBox="0 0 800 1000"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="800" height="1000" fill="#ffffff" />

                <text
                  x="40"
                  y="70"
                  font-family="Arial, sans-serif"
                  font-size="32"
                  font-weight="bold"
                  fill="#002b5c"
                >
                  HOW TO BOOK A BANGALORE TO MYSORE
                </text>
                <text
                  x="40"
                  y="115"
                  font-family="Arial, sans-serif"
                  font-size="32"
                  font-weight="bold"
                  fill="#002b5c"
                >
                  TAXI ON MAKEMYTRIP
                </text>
                <text
                  x="40"
                  y="155"
                  font-family="Arial, sans-serif"
                  font-size="20"
                  fill="#444"
                >
                  Your stress-free 150km journey starts here
                </text>

                <g transform="translate(520, 90)">
                  <circle cx="0" cy="20" r="12" fill="#002b5c" />
                  <path
                    d="M 15 20 Q 55 -10 95 20"
                    fill="none"
                    stroke="#666"
                    stroke-width="2"
                    stroke-dasharray="4,4"
                  />
                  <circle cx="110" cy="20" r="12" fill="#d93025" />
                  <text
                    x="-35"
                    y="55"
                    font-family="Arial"
                    font-size="12"
                    font-weight="bold"
                    fill="#333"
                  >
                    BANGALORE
                  </text>
                  <text
                    x="90"
                    y="55"
                    font-family="Arial"
                    font-size="12"
                    font-weight="bold"
                    fill="#333"
                  >
                    MYSORE
                  </text>
                </g>

                <line
                  x1="160"
                  y1="280"
                  x2="160"
                  y2="850"
                  stroke="#002b5c"
                  stroke-width="2"
                  stroke-dasharray="5,5"
                />

                <g transform="translate(170, 250)">
                  <rect
                    width="320"
                    height="60"
                    rx="30"
                    fill="white"
                    stroke="#002b5c"
                    stroke-width="2"
                  />
                  <circle cx="-10" cy="30" r="30" fill="#d93025" />
                  <text
                    x="-22"
                    y="38"
                    fill="white"
                    font-family="Arial"
                    font-size="22"
                    font-weight="bold"
                  >
                    01
                  </text>
                  <text
                    x="45"
                    y="38"
                    font-family="Arial"
                    font-size="16"
                    font-weight="bold"
                    fill="#333"
                  >
                    OPEN APP OR WEBSITE
                  </text>
                </g>

                <g transform="translate(170, 340)">
                  <rect
                    width="320"
                    height="60"
                    rx="30"
                    fill="white"
                    stroke="#002b5c"
                    stroke-width="2"
                  />
                  <circle cx="-10" cy="30" r="30" fill="#d93025" />
                  <text
                    x="-22"
                    y="38"
                    fill="white"
                    font-family="Arial"
                    font-size="22"
                    font-weight="bold"
                  >
                    02
                  </text>
                  <text
                    x="45"
                    y="38"
                    font-family="Arial"
                    font-size="16"
                    font-weight="bold"
                    fill="#333"
                  >
                    ENTER TRAVEL DETAILS
                  </text>
                </g>

                <g transform="translate(170, 430)">
                  <rect
                    width="320"
                    height="60"
                    rx="30"
                    fill="white"
                    stroke="#002b5c"
                    stroke-width="2"
                  />
                  <circle cx="-10" cy="30" r="30" fill="#d93025" />
                  <text
                    x="-22"
                    y="38"
                    fill="white"
                    font-family="Arial"
                    font-size="22"
                    font-weight="bold"
                  >
                    03
                  </text>
                  <text
                    x="45"
                    y="38"
                    font-family="Arial"
                    font-size="16"
                    font-weight="bold"
                    fill="#333"
                  >
                    VIEW CAB OPTIONS
                  </text>
                </g>

                <g transform="translate(170, 560)">
                  <rect
                    width="320"
                    height="60"
                    rx="30"
                    fill="white"
                    stroke="#002b5c"
                    stroke-width="2"
                  />
                  <circle cx="-10" cy="30" r="30" fill="#d93025" />
                  <text
                    x="-22"
                    y="38"
                    fill="white"
                    font-family="Arial"
                    font-size="22"
                    font-weight="bold"
                  >
                    05
                  </text>
                  <text
                    x="45"
                    y="38"
                    font-family="Arial"
                    font-size="16"
                    font-weight="bold"
                    fill="#333"
                  >
                    PICK YOUR RIDE
                  </text>
                </g>

                <g transform="translate(170, 650)">
                  <rect
                    width="320"
                    height="60"
                    rx="30"
                    fill="white"
                    stroke="#002b5c"
                    stroke-width="2"
                  />
                  <circle cx="-10" cy="30" r="30" fill="#d93025" />
                  <text
                    x="-22"
                    y="38"
                    fill="white"
                    font-family="Arial"
                    font-size="22"
                    font-weight="bold"
                  >
                    06
                  </text>
                  <text
                    x="45"
                    y="38"
                    font-family="Arial"
                    font-size="16"
                    font-weight="bold"
                    fill="#333"
                  >
                    SECURE PAYMENT
                  </text>
                </g>

                <g transform="translate(170, 740)">
                  <rect
                    width="320"
                    height="60"
                    rx="30"
                    fill="white"
                    stroke="#002b5c"
                    stroke-width="2"
                  />
                  <circle cx="-10" cy="30" r="30" fill="#d93025" />
                  <text
                    x="-22"
                    y="38"
                    fill="white"
                    font-family="Arial"
                    font-size="22"
                    font-weight="bold"
                  >
                    07
                  </text>
                  <text
                    x="45"
                    y="38"
                    font-family="Arial"
                    font-size="16"
                    font-weight="bold"
                    fill="#333"
                  >
                    READY TO GO!
                  </text>
                </g>

                <g transform="translate(560, 400)">
                  <rect
                    width="130"
                    height="400"
                    rx="10"
                    fill="white"
                    stroke="#d93025"
                    stroke-width="3"
                  />
                  <path
                    d="M0 10 Q0 0 10 0 L120 0 Q130 0 130 10 L130 45 L0 45 Z"
                    fill="#d93025"
                  />
                  <text
                    x="25"
                    y="30"
                    fill="white"
                    font-family="Arial"
                    font-size="16"
                    font-weight="bold"
                  >
                    THE FLEET
                  </text>

                  <text
                    x="25"
                    y="100"
                    font-family="Arial"
                    font-size="12"
                    font-weight="bold"
                    fill="#333"
                  >
                    HATCHBACK
                  </text>
                  <line x1="15" y1="110" x2="115" y2="110" stroke="#ddd" />
                  <text
                    x="40"
                    y="160"
                    font-family="Arial"
                    font-size="12"
                    font-weight="bold"
                    fill="#333"
                  >
                    SEDAN
                  </text>
                  <line x1="15" y1="170" x2="115" y2="170" stroke="#ddd" />
                  <text
                    x="50"
                    y="220"
                    font-family="Arial"
                    font-size="12"
                    font-weight="bold"
                    fill="#333"
                  >
                    SUV
                  </text>
                  <line x1="15" y1="230" x2="115" y2="230" stroke="#ddd" />
                  <text
                    x="50"
                    y="280"
                    font-family="Arial"
                    font-size="12"
                    font-weight="bold"
                    fill="#333"
                  >
                    SUV
                  </text>
                  <line x1="15" y1="290" x2="115" y2="290" stroke="#ddd" />
                  <text
                    x="18"
                    y="340"
                    font-family="Arial"
                    font-size="12"
                    font-weight="bold"
                    fill="#333"
                  >
                    TEMPO TRAVELER
                  </text>
                </g>

                <rect x="0" y="910" width="800" height="90" fill="#002b5c" />
                <text
                  x="50"
                  y="960"
                  fill="white"
                  font-family="Arial"
                  font-size="15"
                  font-weight="bold"
                >
                  24/7 SUPPORT
                </text>
                <text
                  x="240"
                  y="960"
                  fill="white"
                  font-family="Arial"
                  font-size="15"
                  font-weight="bold"
                >
                  VERIFIED DRIVERS
                </text>
                <text
                  x="450"
                  y="960"
                  fill="white"
                  font-family="Arial"
                  font-size="15"
                  font-weight="bold"
                >
                  CLEAN CABS
                </text>

                <rect
                  x="630"
                  y="930"
                  width="150"
                  height="50"
                  rx="5"
                  fill="#d93025"
                />
                <text
                  x="640"
                  y="960"
                  fill="white"
                  font-family="Arial"
                  font-size="10"
                  font-weight="bold"
                >
                  BOOK NOW &amp; TRAVEL HAPPILY
                </text>
              </svg>
            </div> */}
            {/* <RouteInfoCards
              sourceCityName={sourceCityName}
              destinationCityName={destinationCityName}
              inboundCallNumber={inboundCallNumberFinal}
            /> */}
            {/* Route Info Tabs */}
            {/* <AuthorBio
              sourceCityName={sourceCityName}
              destinationCityName={destinationCityName}
            /> */}
            {templateData.howToBook ? (
              <>
                <section id="descriptionInHtml">
                  <h2>{`How to Book a Taxi from ${sourceCityName} to ${destinationCityName} on MakeMyTrip? `}</h2>
                  <div>
                    <ThingsToKnow html={templateData.howToBook} />
                  </div>
                </section>
              </>
            ) : (
              <>
                <section className="hideSection"></section>
              </>
            )}
            {topRoutesfromCity.cabTypes.length > 0 && !isomorph ? (
              <TopCabsRoutes
                h2={`Top ${
                  allowRoundtrip ? 'Round Trip ' : ''
                }Cab Routes from ${sourceCityName}`}
                cityName={sourceCityName}
                routeType="From"
                data={topRoutesfromCity}
                enableAnchorTags={true}
              />
            ) : (
              <section className="hideSection"></section>
            )}
            {topRoutesToCity.cabTypes.length > 0 && !isomorph ? (
              <TopCabsRoutes
                h2={`Top ${
                  allowRoundtrip ? 'Round Trip ' : ''
                }Cab Routes to ${destinationCityName}`}
                cityName={destinationCityName}
                routeType="To"
                data={topRoutesToCity}
                enableAnchorTags={true}
              />
            ) : (
              <section className="hideSection"></section>
            )}
            {cabTypesAvailableForRent &&
            cabTypesAvailableForRent.packages &&
            cabTypesAvailableForRent.packages.length > 0 &&
            !isomorph ? (
              <RentalCabs
                h2={`${sourceCityName} to ${destinationCityName} Rental Cabs`}
                cityName={sourceCityName}
                data={cabTypesAvailableForRent}
              />
            ) : (
              <section className="hideSection"></section>
            )}
            {templateData.descriptionInHtml &&
            topRoutesInfoFromSourceToDestination.cabsList.length > 0 ? (
              <section id="descriptionInHtml" className="visibilityAuto">
                <ThingsToKnow html={templateData.descriptionInHtml} />
              </section>
            ) : null}
            {routeInfoData &&
              routeInfoData?.table &&
              routeInfoData?.table.length > 0 && (
                <section className="visibilityAuto">
                  <h2>{routeInfoData?.title}</h2>
                  <div>
                    <table className="routeInfoTable">
                      <thead>
                        <tr className="tableRow">
                          {routeInfoData?.table[0] && (
                            <>
                              <th className="tableData">
                                {routeInfoData.table[0]?.key}
                              </th>
                              <th className="tableData">
                                {routeInfoData.table[0]?.value}
                              </th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody style={{ width: '100%' }}>
                        {routeInfoData?.table?.slice(1)?.map((item, index) => (
                          <tr key={index} className="tableRow">
                            <td className="tableData">
                              <p className="tableDatakey">{item?.key}</p>
                            </td>
                            <td className="tableData">
                              <p>{item?.value}</p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            {description && (
              <section className="visibilityAuto">
                <h2>{`${sourceCityName} to ${destinationCityName} Cab Booking on MMT`}</h2>
                <ThingsToKnow html={description} />
              </section>
            )}
            {(sourceCityDescription || destinationCityDescription) && (
              <section className="visibilityAuto">
                <h2>
                  {`Things To Know About
              ${(() => {
                if (sourceCityDescription && destinationCityDescription) {
                  return `${sourceCityName} and ${destinationCityName}`;
                } else if (sourceCityDescription) {
                  return `${sourceCityName}`;
                } else if (destinationCityDescription) {
                  return `${destinationCityName}`;
                }
              })()}`}
                </h2>
                <ThingsToKnow
                  text1={sourceCityDescription ? sourceCityDescription : ''}
                  text2={
                    destinationCityDescription ? destinationCityDescription : ''
                  }
                />
              </section>
            )}
            {templateData.faq.length > 0 && (
              <section className="visibilityAuto">
                <h2>{`Frequently Asked Questions for ${sourceCityName} to ${destinationCityName} Cab Booking`}</h2>
                <FaqSection
                  trackTemplateEvent={trackRouteTemplateEvent}
                  data={templateData.faq}
                  fromCityCode={sourceCityCode}
                  toCityCode={destinationCityCode}
                  pageUrl={templateData.sampleUrl}
                  schemaFaqs={getRouteInfoTabFaqs(
                    sourceCityName,
                    destinationCityName
                  )}
                />
              </section>
            )}
            {templateData.faq &&
              templateData.faq.filter(faq => faq.type === 'LLM').length > 0 && (
                <section className="visibilityAuto">
                  <h2>{`Popular Questions About Traveling from ${sourceCityName} to ${destinationCityName}`}</h2>
                  <FaqSection
                    trackTemplateEvent={trackRouteTemplateEvent}
                    data={templateData.faq}
                    fromCityCode={sourceCityCode}
                    toCityCode={destinationCityCode}
                    pageUrl={templateData.sampleUrl}
                    type="LLM"
                  />
                </section>
              )}
            {topRoutesfromCity.cabTypes.length > 0 && isomorph ? (
              <TopCabsRoutes
                h2={`Top Cab Routes from ${sourceCityName}`}
                cityName={sourceCityName}
                routeType="From"
                data={topRoutesfromCity}
                enableAnchorTags={true}
              />
            ) : (
              <section className="hideSection"></section>
            )}
            {topRoutesToCity.cabTypes.length > 0 && isomorph ? (
              <TopCabsRoutes
                h2={`Top Cab Routes to ${destinationCityName}`}
                cityName={destinationCityName}
                routeType="To"
                data={topRoutesToCity}
                enableAnchorTags={true}
              />
            ) : (
              <section className="hideSection"></section>
            )}
            {cabTypesAvailableForRent &&
            cabTypesAvailableForRent.packages &&
            cabTypesAvailableForRent.packages.length > 0 &&
            isomorph ? (
              <RentalCabs
                h2={`${sourceCityName} to ${destinationCityName} Rental Cabs`}
                cityName={sourceCityName}
                data={cabTypesAvailableForRent}
              />
            ) : (
              <section className="hideSection"></section>
            )}
            <div
              style={{ flexWrap: 'wrap' }}
              className={`cabsSeoWrap__Details makeFlex visibilityAuto`}
            >
              {interlinks &&
                interlinks.map(({ title, links }) => {
                  return (
                    <CompactList
                      titlePrefix={title}
                      titleSuffix={``}
                      list={links}
                      type={'bullets'}
                      className={'rightMargin'}
                    >
                      {(item: any, idx, style) => (
                        <li
                          key={`${item.text}_${idx}`}
                          className={`${
                            idx ? `paddingT10 paddingB10` : `paddingB10`
                          }`}
                        >
                          {item.url ? (
                            <a
                              className={`linkBoxFont `}
                              onClick={() => {
                                //TO DO: add omniture events from Dataloader
                              }}
                              href={item.url}
                            >
                              {item.text}
                            </a>
                          ) : (
                            <span>{item.text}</span>
                          )}
                        </li>
                      )}
                    </CompactList>
                  );
                })}
            </div>
          </div>
        </main>
        {/* {selectedKnowMoreDetailsForMobile ? (
          <Overlay
            cancelButton={() => delayedKnowMoreDetailsForMobile(null)}
            knowMoreDetails={selectedKnowMoreDetailsForMobile}
          />
        ) : null} */}
        <Footer template={templateData} />
        {/* <div
          className="blackOverlay"
          onClick={() => {
            delayedKnowMoreDetailsForMobile(null);
          }}
        ></div> */}
        {isOverlayVisible && (
          <div
            onClick={() => {
              toggleOverlay(false);
            }}
            className="searchWidgetModal mobileShow"
          ></div>
        )}
        {isMobileLoginOverlayVisible && isMobileDevice && (
          <div
            onClick={() => {
              toggleMobileLoginOverlay(false);
            }}
            className="mobileLogin mobileShow"
          ></div>
        )}
      </div>
    </AppContextProvider>
  );
};

RouteTemplate.fetchData = async (_, match, urlObj) => {
  const {
    params: { routeId }
  } = match;
  // if url contains showllmFaq=true then add it to the urlObj.query, then pass it to the data loader
  let query = urlObj.query;
  if (query.includes('showllmFaq=true')) {
    query = query.concat('&showLLMFaqs=true');
  }
  const url = `${DATA_LOADER_URL}/api/routeTemplateData/${routeId}?${query}`;
  let responseData;
  const response = await fetch(url);
  const textResponse = await response.text();

  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new DataLoaderError(
      `Unable to fetch city details of ${routeId}`,
      responseData.error
    );
  }
  return responseData;
};
export default RouteTemplate;
function getBotVisibilityAndSchemaStrategy(
  useAPILevelReviewSchema: number | boolean,
  VARIANT_2: string,
  VARIANT_1: string
) {
  let usedVariant: string | null = null;

  if (useAPILevelReviewSchema == 2) {
    usedVariant = VARIANT_2;
  } else if (useAPILevelReviewSchema == 1) {
    usedVariant = VARIANT_1;
  }
  return usedVariant;
}
